<script setup>
import { computed, inject, onMounted, ref, watch } from "vue";
import { useMainStore } from "@/stores/main.js";
import { mapPeriodToDateRange } from "@/utils";
import { Competitor, Review } from "@/services";
import MHeader from "@/components/m/MHeader.vue";
import MPeriodScope from "@/components/m/MPeriodScope.vue";
import MPullScroll from "@/components/m/MPullScroll.vue";
import MIcon from "@/components/m/MIcon.vue";
import MSheet from "@/components/m/MSheet.vue";
import MToast from "@/components/m/MToast.vue";

// ctx is injected for consistency with the other screens (header/tabs context)
inject("mctx");
const mainStore = useMainStore();

const COLOR_PALETTE = [
  "var(--info)",
  "var(--violet)",
  "var(--pos)",
  "var(--neg)",
];
const TREND_COLORS = [
  "var(--info)",
  "var(--violet)",
  "var(--pos)",
  "var(--neg)",
  "var(--neu)",
];

// metric definitions: higher=true means a larger value is better.
const QUALITY_METRICS = [
  {
    key: "avg_rating",
    label: "Avg. Rating",
    higher: true,
    fmt: (v) => Number(v).toFixed(2) + "★",
    deltaFmt: (d) => (d >= 0 ? "+" : "−") + Math.abs(d).toFixed(2),
  },
  {
    key: "median_response_time_hrs",
    label: "Response Time",
    higher: false,
    fmt: (v) => Number(v).toFixed(1) + "h",
    deltaFmt: (d) => (d >= 0 ? "+" : "−") + Math.abs(d).toFixed(1) + "h",
  },
  {
    key: "negative_reviews_percentage",
    label: "Negative %",
    higher: false,
    fmt: (v) => Number(v).toFixed(1) + "%",
    deltaFmt: (d) => (d >= 0 ? "+" : "−") + Math.abs(d).toFixed(1) + "pt",
  },
  {
    key: "avg_sentiment",
    label: "Sentiment",
    higher: true,
    fmt: (v) => (Number(v) >= 0 ? "+" : "") + Number(v).toFixed(2),
    deltaFmt: (d) => (d >= 0 ? "+" : "−") + Math.abs(d).toFixed(2),
  },
];
const MARKET_METRICS = [
  {
    key: "reviews_count",
    label: "Review Volume",
    higher: true,
    fmt: (v) => Number(v || 0).toLocaleString(),
    deltaFmt: (d) => (d >= 0 ? "+" : "−") + Math.abs(d).toLocaleString(),
  },
];
const METRICS = [...QUALITY_METRICS, ...MARKET_METRICS];

const isNum = (v) =>
  v !== null && v !== undefined && Number.isFinite(Number(v));
const metricByKey = (key) => METRICS.find((m) => m.key === key);

const loading = ref(false);
const rows = computed(() => mainStore.competitorData || []);
const youId = computed(() => String(mainStore.selectedCompany?.company_id));
const you = computed(
  () => rows.value.find((r) => String(r.company_id) === youId.value) || null,
);
const others = computed(() =>
  rows.value.filter((r) => String(r.company_id) !== youId.value),
);

const sortedRows = computed(() => {
  const mine = rows.value.filter((r) => String(r.company_id) === youId.value);
  const rest = rows.value
    .filter((r) => String(r.company_id) !== youId.value)
    .sort(
      (a, b) => Number(b.reviews_count || 0) - Number(a.reviews_count || 0),
    );
  return [...mine, ...rest];
});
function colorFor(companyId) {
  if (String(companyId) === youId.value) return "var(--brand)";
  const idx = sortedRows.value
    .filter((r) => String(r.company_id) !== youId.value)
    .findIndex((r) => String(r.company_id) === String(companyId));
  return COLOR_PALETTE[(idx < 0 ? 0 : idx) % COLOR_PALETTE.length];
}

// --- Gap cards (you vs. best rival, per metric) ---
const gapCards = computed(() => {
  if (!you.value || !others.value.length) return [];
  return METRICS.map((m) => {
    if (!isNum(you.value[m.key])) return null;
    const youV = Number(you.value[m.key]);
    const candidates = others.value
      .map((r) => ({ name: r.company_name, v: r[m.key] }))
      .filter((c) => isNum(c.v));
    if (!candidates.length) return null;
    const best = candidates.reduce((a, b) =>
      m.higher
        ? Number(b.v) > Number(a.v)
          ? b
          : a
        : Number(b.v) < Number(a.v)
          ? b
          : a,
    );
    const bestV = Number(best.v);
    const behind = m.higher ? youV < bestV : youV > bestV;
    // meter fraction driven by the real gap (parity = 0.5, ahead > 0.5).
    let yv = youV;
    let bv = bestV;
    if (m.key === "avg_sentiment") {
      yv += 1; // shift -1..1 → 0..2 so the ratio stays meaningful
      bv += 1;
    }
    const total = yv + bv;
    let frac = total ? (m.higher ? yv / total : bv / total) : 0.5;
    frac = Math.max(0.08, Math.min(0.96, frac));
    return {
      metric: m.label,
      key: m.key,
      you: m.fmt(youV),
      best: m.fmt(bestV),
      who: best.name,
      delta: m.deltaFmt(youV - bestV),
      rawDelta: youV - bestV,
      behind,
      frac,
    };
  }).filter(Boolean);
});

const banner = computed(() => {
  if (!you.value) return null;
  if (!others.value.length)
    return {
      tone: "neu",
      text: "Add competitors to benchmark against rivals.",
      sub: "",
    };
  const behindCards = gapCards.value.filter((c) => c.behind);
  if (!behindCards.length)
    return {
      tone: "pos",
      text: "You lead on all tracked quality metrics.",
      sub: "Keep monitoring — rivals move fast.",
    };
  const counts = {};
  behindCards.forEach((c) => (counts[c.who] = (counts[c.who] || 0) + 1));
  const topWho = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  return {
    tone: "neg",
    text: `${topWho} beats you on ${behindCards.length} of ${gapCards.value.length} quality metrics.`,
    sub: "Closing the response-time gap is the fastest win.",
  };
});

const biggestGap = computed(() => {
  const lossesBySize = gapCards.value
    .filter((c) => c.behind)
    .sort(
      (a, b) => Math.abs(Number(b.rawDelta)) - Math.abs(Number(a.rawDelta)),
    );
  return lossesBySize[0] || null;
});

const closestWin = computed(() => {
  const winsBySize = gapCards.value
    .filter((c) => !c.behind)
    .sort(
      (a, b) => Math.abs(Number(a.rawDelta)) - Math.abs(Number(b.rawDelta)),
    );
  return winsBySize[0] || null;
});

function verdict(row) {
  if (!you.value) return null;
  if (String(row.company_id) === youId.value) {
    const behindCount = gapCards.value.filter((c) => c.behind).length;
    return behindCount
      ? { cls: "neg", text: `Behind ${behindCount}/${gapCards.value.length}` }
      : { cls: "pos", text: "Leading" };
  }
  let beats = 0;
  let n = 0;
  QUALITY_METRICS.forEach((m) => {
    if (!isNum(row[m.key]) || !isNum(you.value[m.key])) return;
    n += 1;
    const better = m.higher
      ? Number(row[m.key]) > Number(you.value[m.key])
      : Number(row[m.key]) < Number(you.value[m.key]);
    if (better) beats += 1;
  });
  return beats * 2 >= n && n > 0
    ? { cls: "pos", text: "Leading" }
    : { cls: "neu", text: "Trailing" };
}

// --- Per-metric rank (1 = best in the benchmark set) ---
function metricRank(row, metric) {
  if (!metric || !isNum(row?.[metric.key])) return null;
  const ranked = sortedRows.value
    .filter((item) => isNum(item[metric.key]))
    .sort((a, b) =>
      metric.higher
        ? Number(b[metric.key]) - Number(a[metric.key])
        : Number(a[metric.key]) - Number(b[metric.key]),
    );
  const idx = ranked.findIndex(
    (item) => String(item.company_id) === String(row.company_id),
  );
  return idx >= 0 ? idx + 1 : null;
}

function cells(row) {
  return [
    {
      key: "reviews_count",
      label: "Reviews",
      value: Number(row.reviews_count || 0).toLocaleString(),
    },
    {
      key: "avg_rating",
      label: "Rating",
      value: isNum(row.avg_rating)
        ? Number(row.avg_rating).toFixed(2) + "★"
        : "-",
    },
    {
      key: "avg_sentiment",
      label: "Sent.",
      value: isNum(row.avg_sentiment)
        ? (Number(row.avg_sentiment) >= 0 ? "+" : "") +
          Number(row.avg_sentiment).toFixed(2)
        : "-",
    },
    {
      key: "negative_reviews_percentage",
      label: "Neg %",
      value: isNum(row.negative_reviews_percentage)
        ? Number(row.negative_reviews_percentage).toFixed(1) + "%"
        : "-",
    },
    {
      key: "median_response_time_hrs",
      label: "Response",
      value: isNum(row.median_response_time_hrs)
        ? Number(row.median_response_time_hrs).toFixed(1) + "h"
        : "-",
    },
  ].map((c) => ({ ...c, rank: metricRank(row, metricByKey(c.key)) }));
}

// --- Benchmark score (composite, lightweight) + rank ---
function reputationLite(row) {
  if (!row) return 0;
  const rating = isNum(row.avg_rating) ? (Number(row.avg_rating) / 5) * 35 : 0;
  const sentiment = isNum(row.avg_sentiment)
    ? ((Number(row.avg_sentiment) + 1) / 2) * 25
    : 0;
  const negative = isNum(row.negative_reviews_percentage)
    ? Math.max(0, 100 - Number(row.negative_reviews_percentage)) * 0.25
    : 0;
  const response = isNum(row.response_speed_factor)
    ? Number(row.response_speed_factor) * 0.15
    : 0;
  return Math.round(
    Math.max(0, Math.min(100, rating + sentiment + negative + response)),
  );
}

const rankedScores = computed(() =>
  sortedRows.value
    .map((row) => ({ row, score: reputationLite(row) }))
    .sort((a, b) => b.score - a.score),
);
const scoreForYou = computed(() =>
  rankedScores.value.find(
    (item) => String(item.row.company_id) === youId.value,
  ),
);
const yourRank = computed(() =>
  scoreForYou.value ? rankedScores.value.indexOf(scoreForYou.value) + 1 : null,
);
const strongestCompetitor = computed(() =>
  rankedScores.value.find(
    (item) => String(item.row.company_id) !== youId.value,
  ),
);

// --- Comparison bars (preserved) ---
const ratingBars = computed(() =>
  sortedRows.value.map((r) => ({
    name: r.company_name,
    you: String(r.company_id) === youId.value,
    v: Number(r.avg_rating || 0),
    value: Number(r.avg_rating || 0).toFixed(2),
    color: colorFor(r.company_id),
  })),
);
const negBars = computed(() =>
  sortedRows.value.map((r) => ({
    name: r.company_name,
    you: String(r.company_id) === youId.value,
    v: Number(r.negative_reviews_percentage || 0),
    value: Number(r.negative_reviews_percentage || 0).toFixed(1) + "%",
    color: colorFor(r.company_id),
  })),
);

// --- Benchmark trend (sparkline, volume / rating toggle) ---
const activeTrend = ref("volume");
const trendData = ref({ dates: [], companies: [] });

function indexedSeries(values) {
  const nums = (values || []).map((value) =>
    isNum(value) ? Number(value) : null,
  );
  const first = nums.find((value) => value !== null && value > 0);
  if (!first) return nums.map((value) => (value === null ? null : 0));
  return nums.map((value) =>
    value === null ? null : Math.round((value / first) * 100),
  );
}

const trendRows = computed(() =>
  (trendData.value.companies || []).map((company, index) => ({
    name: company.company_name,
    companyId: company.company_id,
    you: String(company.company_id) === youId.value,
    data:
      activeTrend.value === "rating"
        ? company.avg_rating || []
        : indexedSeries(company.volume || []),
    total: (company.volume || []).reduce((acc, v) => acc + Number(v || 0), 0),
    current:
      activeTrend.value === "rating"
        ? (company.avg_rating || []).filter(isNum).at(-1)
        : indexedSeries(company.volume || [])
            .filter(isNum)
            .at(-1),
    color:
      String(company.company_id) === youId.value
        ? "var(--brand)"
        : TREND_COLORS[index % TREND_COLORS.length],
  })),
);

// Build an SVG path for a mini sparkline normalised to its own min/max.
function sparkPath(data, w, h) {
  const nums = (data || []).map((v) => (isNum(v) ? Number(v) : null));
  const valid = nums.filter((v) => v !== null);
  if (valid.length < 2) return "";
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const span = max - min || 1;
  const n = nums.length;
  const step = n > 1 ? w / (n - 1) : w;
  let d = "";
  let started = false;
  nums.forEach((v, i) => {
    if (v === null) return;
    const x = i * step;
    const y = h - ((v - min) / span) * h;
    d += (started ? " L" : "M") + x.toFixed(1) + " " + y.toFixed(1);
    started = true;
  });
  return d;
}

// --- Competitor management ---
const competitorLinks = computed(() => {
  const owner = (mainStore.companiesWatched || []).find(
    (c) => String(c.company_id) === youId.value,
  );
  return owner?.competitors || [];
});

function linkIdFor(companyId) {
  const link = competitorLinks.value.find(
    (c) => String(c.company_id) === String(companyId),
  );
  return link?.link_id ?? null;
}

async function removeCompetitor(linkId, name) {
  if (!confirm(`Remove ${name} as a competitor? This cannot be undone.`))
    return;
  try {
    await Competitor.remove(linkId);
    await mainStore.fetchCompaniesWatched();
    await refresh();
    showToast(`${name} removed.`);
  } catch (_err) {
    showToast(`Failed to remove ${name}.`);
  }
}

async function promoteCompetitor(linkId, name) {
  if (
    !confirm(
      `Promote ${name} to an owned company? It will move to your main company list.`,
    )
  )
    return;
  try {
    await Competitor.promote(linkId);
    await mainStore.fetchCompaniesWatched();
    await refresh();
    showToast(`${name} promoted.`);
  } catch (_err) {
    showToast(`Failed to promote ${name}.`);
  }
}

// --- Research-and-create new-competitor wizard ---
const wizardOpen = ref(false);
const wizardStep = ref(1);
const wizardOwnerCompanyId = ref(null);
const wizardName = ref("");
const wizardWebsite = ref("");
const wizardResearching = ref(false);
const wizardChannels = ref([]);
const wizardSaving = ref(false);
const wizardError = ref("");

function openWizard() {
  wizardOwnerCompanyId.value = mainStore.selectedCompany?.company_id ?? null;
  wizardStep.value = 1;
  wizardName.value = "";
  wizardWebsite.value = "";
  wizardChannels.value = [];
  wizardError.value = "";
  wizardOpen.value = true;
}
function closeWizard() {
  wizardOpen.value = false;
}

async function doResearch() {
  if (!wizardName.value.trim()) {
    wizardError.value = "Name is required.";
    return;
  }
  wizardError.value = "";
  wizardResearching.value = true;
  try {
    const result = await Competitor.research(
      wizardOwnerCompanyId.value,
      wizardName.value.trim(),
      wizardWebsite.value.trim(),
    );
    wizardChannels.value = (result.entities || []).map((e) => ({
      ...e,
      selected: true,
    }));
    wizardStep.value = 2;
  } catch (_err) {
    wizardError.value =
      "Research failed. You can skip this step to add manually.";
    wizardChannels.value = [];
    wizardStep.value = 2;
  } finally {
    wizardResearching.value = false;
  }
}

async function doCreate() {
  wizardSaving.value = true;
  wizardError.value = "";
  try {
    const channels = wizardChannels.value
      .filter((c) => c.selected)
      .map(({ source, platform_id, name }) => ({ source, platform_id, name }));
    const savedName = wizardName.value.trim();
    await Competitor.create(
      wizardOwnerCompanyId.value,
      savedName,
      wizardWebsite.value.trim(),
      channels,
    );
    closeWizard();
    // Refreshing the watched payload re-derives the benchmark set, so the
    // just-created competitor enters the comparison on its own.
    await mainStore.fetchCompaniesWatched();
    await refresh();
    showToast(`${savedName} added as a competitor.`);
  } catch (_err) {
    wizardError.value = "Failed to save. Please try again.";
  } finally {
    wizardSaving.value = false;
  }
}

// --- Toast ---
const toast = ref("");
let toastTimer = null;
function showToast(msg) {
  toast.value = msg;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toast.value = ""), 2400);
}

async function refresh() {
  loading.value = true;
  try {
    await mainStore.updateCompetitorData();
    const ids = (mainStore.selectedCompetitors || [])
      .map((company) => company.company_id)
      .filter(Boolean);
    const { startDate, endDate } = mapPeriodToDateRange(
      mainStore.selectedPeriod,
      mainStore.customRange,
    );
    trendData.value = await Review.getCompetitorTrend(
      ids,
      (mainStore.selectedAggregation || "Monthly").toLowerCase(),
      startDate ? startDate.toISOString() : null,
      endDate ? endDate.toISOString() : null,
    );
  } catch (_err) {
    trendData.value = { dates: [], companies: [] };
  } finally {
    loading.value = false;
  }
}

watch(
  () => [mainStore.dateFilterKey, mainStore.selectedCompany?.company_id],
  refresh,
);
onMounted(refresh);
</script>

<template>
  <div class="btm-app">
    <MHeader
      title="Compare"
      :sub="`You vs. ${Math.max(0, rows.length - 1)} tracked rival${rows.length === 2 ? '' : 's'}`"
    />
    <MPeriodScope />
    <MPullScroll :busy="loading" @refresh="refresh">
      <div class="btm-pad" style="padding-bottom: 16px">
        <!-- benchmark toolbar -->
        <div
          class="btm-card btm-cardpad mc-toolbar"
          style="margin-bottom: 14px"
        >
          <span class="mc-toolbar-ico"
            ><MIcon name="competitors" :size="16"
          /></span>
          <div style="flex: 1; font-weight: 800; font-size: 13px">
            Benchmark set
          </div>
          <button
            class="btm-btn btm-btn-primary btm-btn-sm"
            type="button"
            @click="openWizard"
          >
            <MIcon name="plus" :size="14" :sw="2.4" />Add
          </button>
        </div>

        <!-- gap banner -->
        <div
          v-if="banner"
          class="btm-card"
          :style="{
            background:
              banner.tone === 'neg'
                ? 'var(--neg-soft)'
                : banner.tone === 'pos'
                  ? 'var(--pos-soft)'
                  : 'var(--surface)',
            borderColor: 'transparent',
            padding: '14px',
            marginBottom: '14px',
            display: 'flex',
            gap: '11px',
            alignItems: 'center',
          }"
        >
          <span
            style="
              width: 30px;
              height: 30px;
              border-radius: 9px;
              display: grid;
              place-items: center;
              color: #fff;
              flex: 0 0 auto;
            "
            :style="{
              background:
                banner.tone === 'neg'
                  ? 'var(--neg)'
                  : banner.tone === 'pos'
                    ? 'var(--pos)'
                    : 'var(--neu)',
            }"
          >
            <MIcon name="target" :size="16" />
          </span>
          <div style="font-size: 13px; line-height: 1.45">
            <b>{{ banner.text }}</b
            ><template v-if="banner.sub"> {{ banner.sub }}</template>
          </div>
        </div>

        <!-- benchmark score + biggest gap / closest win -->
        <div
          v-if="you && others.length"
          style="
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 11px;
            margin-bottom: 14px;
          "
        >
          <div class="btm-card btm-cardpad" style="grid-column: 1 / -1">
            <div class="btm-eyebrow" style="font-size: 9px">
              Benchmark Score
            </div>
            <div
              style="
                display: flex;
                align-items: flex-end;
                gap: 12px;
                margin-top: 6px;
              "
            >
              <span
                class="btm-num"
                style="
                  font-size: 34px;
                  font-weight: 900;
                  line-height: 1;
                  color: var(--brand-strong);
                "
                >{{ scoreForYou?.score ?? 0 }}</span
              >
              <div
                class="btm-muted"
                style="font-size: 11px; padding-bottom: 3px"
              >
                Rank #{{ yourRank ?? "-" }} of {{ rankedScores.length || 0 }}
              </div>
              <div
                v-if="strongestCompetitor"
                style="margin-left: auto; text-align: right"
              >
                <div class="btm-eyebrow" style="font-size: 8.5px">
                  Strongest rival
                </div>
                <div style="font-size: 12.5px; font-weight: 800">
                  {{ strongestCompetitor.row.company_name }}
                </div>
                <div class="btm-muted" style="font-size: 11px">
                  {{ strongestCompetitor.score }}/100
                </div>
              </div>
            </div>
          </div>

          <div class="btm-card btm-cardpad">
            <div class="btm-eyebrow" style="font-size: 9px">Biggest Gap</div>
            <template v-if="biggestGap">
              <div style="font-size: 13px; font-weight: 800; margin: 6px 0 2px">
                {{ biggestGap.metric }}
              </div>
              <div class="btm-num" style="font-size: 15px; font-weight: 800">
                {{ biggestGap.you }} <span class="btm-muted">vs</span>
                {{ biggestGap.best }}
              </div>
              <div class="btm-muted" style="font-size: 11px; margin-top: 3px">
                {{ biggestGap.who }} leads by {{ biggestGap.delta }}.
              </div>
            </template>
            <div
              v-else
              class="btm-muted"
              style="font-size: 12px; margin-top: 6px"
            >
              No trailing metric in this set.
            </div>
          </div>

          <div class="btm-card btm-cardpad">
            <div class="btm-eyebrow" style="font-size: 9px">Closest Win</div>
            <template v-if="closestWin">
              <div style="font-size: 13px; font-weight: 800; margin: 6px 0 2px">
                {{ closestWin.metric }}
              </div>
              <div class="btm-num" style="font-size: 15px; font-weight: 800">
                {{ closestWin.you }} <span class="btm-muted">vs</span>
                {{ closestWin.best }}
              </div>
              <div class="btm-muted" style="font-size: 11px; margin-top: 3px">
                You lead {{ closestWin.who }} by {{ closestWin.delta }}.
              </div>
            </template>
            <div
              v-else
              class="btm-muted"
              style="font-size: 12px; margin-top: 6px"
            >
              No current wins to defend.
            </div>
          </div>
        </div>

        <!-- gap cards -->
        <div
          v-if="gapCards.length"
          style="
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 11px;
            margin-bottom: 18px;
          "
        >
          <div
            v-for="g in gapCards"
            :key="g.metric"
            class="btm-card btm-cardpad"
          >
            <div class="btm-eyebrow" style="font-size: 9px">{{ g.metric }}</div>
            <div
              style="
                display: flex;
                align-items: baseline;
                gap: 6px;
                margin: 8px 0 3px;
              "
            >
              <span class="btm-num" style="font-size: 22px; font-weight: 800">{{
                g.you
              }}</span>
              <span class="btm-delta" :class="g.behind ? 'down' : 'up'"
                ><MIcon
                  :name="g.behind ? 'arrowdownright' : 'arrowupright'"
                  :size="10"
                  :sw="2.4"
                />{{ g.delta }}</span
              >
            </div>
            <div class="btm-muted" style="font-size: 11px">
              vs <b style="color: var(--text-2)">{{ g.best }}</b> · {{ g.who }}
            </div>
            <span class="btm-meter" style="height: 5px; margin-top: 9px"
              ><span
                :style="{
                  width: g.frac * 100 + '%',
                  background: g.behind ? 'var(--neg)' : 'var(--pos)',
                }"
            /></span>
          </div>
        </div>

        <!-- head to head -->
        <div class="btm-cardhead" style="margin-bottom: 10px">
          <div class="t" style="font-size: 17px">Head-to-head</div>
          <span v-if="loading" class="btm-muted" style="font-size: 12px"
            >Loading…</span
          >
        </div>
        <div
          style="
            display: flex;
            flex-direction: column;
            gap: 11px;
            margin-bottom: 18px;
          "
        >
          <div
            v-for="c in sortedRows"
            :key="c.company_id"
            class="btm-card btm-cardpad"
            :style="
              String(c.company_id) === youId
                ? {
                    borderColor: 'var(--brand-line)',
                    background: 'var(--brand-soft)',
                  }
                : null
            "
          >
            <div
              style="
                display: flex;
                align-items: center;
                gap: 9px;
                margin-bottom: 12px;
              "
            >
              <span
                style="width: 10px; height: 10px; border-radius: 50%"
                :style="{ background: colorFor(c.company_id) }"
              />
              <span style="font-weight: 800; font-size: 14.5px">{{
                c.company_name
              }}</span>
              <span v-if="String(c.company_id) === youId" class="btm-tag brand"
                >YOU</span
              >
              <span
                v-if="verdict(c)"
                class="btm-tag"
                :class="verdict(c).cls"
                style="margin-left: auto"
                >{{ verdict(c).text }}</span
              >
            </div>
            <div
              style="
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 10px 8px;
              "
            >
              <div v-for="cell in cells(c)" :key="cell.key">
                <div class="btm-eyebrow" style="font-size: 8.5px">
                  {{ cell.label }}
                </div>
                <div
                  style="
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    margin-top: 3px;
                  "
                >
                  <span
                    class="btm-num btm-mono"
                    style="font-size: 14px; font-weight: 800"
                    >{{ cell.value }}</span
                  >
                  <span v-if="cell.rank" class="mc-rank">#{{ cell.rank }}</span>
                </div>
              </div>
            </div>
            <!-- per-competitor actions -->
            <div v-if="String(c.company_id) !== youId" class="mc-actions">
              <template v-if="linkIdFor(c.company_id) !== null">
                <button
                  type="button"
                  class="mc-act mc-act-neg"
                  @click="
                    removeCompetitor(linkIdFor(c.company_id), c.company_name)
                  "
                >
                  Delete
                </button>
                <button
                  type="button"
                  class="mc-act mc-act-brand"
                  @click="
                    promoteCompetitor(linkIdFor(c.company_id), c.company_name)
                  "
                >
                  Promote
                </button>
              </template>
            </div>
          </div>
          <div
            v-if="!sortedRows.length && !loading"
            class="btm-muted btm-card btm-cardpad"
          >
            No competitor data yet.
          </div>
        </div>

        <!-- benchmark trend -->
        <div
          v-if="trendRows.length"
          class="btm-card btm-cardpad"
          style="margin-bottom: 12px"
        >
          <div class="btm-cardhead" style="margin-bottom: 12px">
            <div>
              <div class="t" style="font-size: 14px">Benchmark Trend</div>
              <div class="s">
                {{ mainStore.selectedAggregation }} per competitor
              </div>
            </div>
          </div>
          <div class="btm-seg" style="margin-bottom: 14px">
            <button
              type="button"
              :class="{ on: activeTrend === 'volume' }"
              @click="activeTrend = 'volume'"
            >
              Volume
            </button>
            <button
              type="button"
              :class="{ on: activeTrend === 'rating' }"
              @click="activeTrend = 'rating'"
            >
              Rating
            </button>
          </div>
          <div style="display: flex; flex-direction: column; gap: 4px">
            <div
              v-for="row in trendRows"
              :key="row.companyId"
              style="
                display: grid;
                grid-template-columns: 86px 1fr 52px;
                gap: 9px;
                align-items: center;
                padding: 8px 0;
                border-top: 1px solid var(--border);
              "
            >
              <span
                :style="{
                  fontSize: '12px',
                  fontWeight: row.you ? 800 : 600,
                  color: row.you ? 'var(--brand-strong)' : 'var(--text-2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }"
                >{{ row.name }}</span
              >
              <svg
                v-if="sparkPath(row.data, 120, 26)"
                viewBox="0 0 120 26"
                preserveAspectRatio="none"
                style="width: 100%; height: 26px; overflow: visible"
              >
                <path
                  :d="sparkPath(row.data, 120, 26)"
                  fill="none"
                  :stroke="row.color"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span v-else class="btm-muted" style="font-size: 11px"
                >No data</span
              >
              <span
                class="btm-num btm-mono"
                style="font-size: 12px; font-weight: 800; text-align: right"
              >
                <template v-if="activeTrend === 'rating'">{{
                  isNum(row.current)
                    ? Number(row.current).toFixed(2) + "★"
                    : "-"
                }}</template>
                <template v-else>{{ row.total.toLocaleString() }}</template>
              </span>
            </div>
          </div>
        </div>

        <!-- comparison bars -->
        <template v-if="sortedRows.length">
          <div class="btm-card btm-cardpad" style="margin-bottom: 12px">
            <div class="btm-cardhead">
              <div>
                <div class="t" style="font-size: 14px">Average Rating</div>
                <div class="s">out of 5 stars</div>
              </div>
            </div>
            <div style="display: flex; flex-direction: column; gap: 11px">
              <div
                v-for="b in ratingBars"
                :key="b.name"
                style="
                  display: grid;
                  grid-template-columns: 76px 1fr 44px;
                  gap: 9px;
                  align-items: center;
                "
              >
                <span
                  :style="{
                    fontSize: '12px',
                    fontWeight: b.you ? 800 : 600,
                    color: b.you ? 'var(--brand-strong)' : 'var(--text-2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }"
                  >{{ b.name }}</span
                >
                <span class="btm-meter" style="height: 10px"
                  ><span
                    :style="{
                      width: (b.v / 5) * 100 + '%',
                      background: b.color,
                    }"
                /></span>
                <span
                  class="btm-num btm-mono"
                  style="font-size: 12px; font-weight: 800; text-align: right"
                  >{{ b.value }}</span
                >
              </div>
            </div>
          </div>
          <div class="btm-card btm-cardpad">
            <div class="btm-cardhead">
              <div>
                <div class="t" style="font-size: 14px">
                  Negative Review Share
                </div>
                <div class="s">lower is better</div>
              </div>
            </div>
            <div style="display: flex; flex-direction: column; gap: 11px">
              <div
                v-for="b in negBars"
                :key="b.name"
                style="
                  display: grid;
                  grid-template-columns: 76px 1fr 44px;
                  gap: 9px;
                  align-items: center;
                "
              >
                <span
                  :style="{
                    fontSize: '12px',
                    fontWeight: b.you ? 800 : 600,
                    color: b.you ? 'var(--brand-strong)' : 'var(--text-2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }"
                  >{{ b.name }}</span
                >
                <span class="btm-meter" style="height: 10px"
                  ><span
                    :style="{
                      width: Math.min(100, (b.v / 40) * 100) + '%',
                      background: b.color,
                    }"
                /></span>
                <span
                  class="btm-num btm-mono"
                  style="font-size: 12px; font-weight: 800; text-align: right"
                  >{{ b.value }}</span
                >
              </div>
            </div>
          </div>
        </template>
      </div>
    </MPullScroll>

    <!-- Research-and-create new competitor wizard -->
    <MSheet :open="wizardOpen" label="New competitor" @close="closeWizard">
      <!-- Step 1: name + website -->
      <template v-if="wizardStep === 1">
        <div style="font-weight: 800; font-size: 16px; margin-bottom: 4px">
          Add a competitor
        </div>
        <div class="btm-muted" style="font-size: 12.5px; margin-bottom: 14px">
          Find and add a new competitor company.
        </div>
        <label class="mc-label" for="wiz-name"
          >Company name <span style="color: var(--neg)">*</span></label
        >
        <input
          id="wiz-name"
          v-model="wizardName"
          class="mc-input"
          type="text"
          placeholder="e.g. Rival Brand"
          @keydown.enter="doResearch"
        />
        <label class="mc-label" for="wiz-website" style="margin-top: 12px"
          >Website (optional)</label
        >
        <input
          id="wiz-website"
          v-model="wizardWebsite"
          class="mc-input"
          type="url"
          placeholder="https://example.com"
        />
        <div
          v-if="wizardError"
          style="color: var(--neg); font-size: 13px; margin-top: 10px"
        >
          {{ wizardError }}
        </div>
        <div style="display: flex; gap: 10px; margin-top: 18px">
          <button
            type="button"
            class="btm-btn btm-btn-ghost"
            style="flex: 1"
            @click="closeWizard"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btm-btn btm-btn-primary"
            style="flex: 1"
            :disabled="wizardResearching"
            @click="doResearch"
          >
            {{ wizardResearching ? "Researching…" : "Research channels" }}
          </button>
        </div>
      </template>

      <!-- Step 2: confirm channels -->
      <template v-else>
        <div style="font-weight: 800; font-size: 16px; margin-bottom: 4px">
          Confirm channels
        </div>
        <div class="btm-muted" style="font-size: 12.5px; margin-bottom: 14px">
          Select the review channels to track for this competitor.
        </div>
        <div
          v-if="wizardChannels.length === 0"
          class="btm-muted"
          style="font-size: 13px"
        >
          No review channels discovered. You can save without channels and add
          them later.
        </div>
        <div
          v-else
          style="
            display: flex;
            flex-direction: column;
            gap: 8px;
            max-height: 46vh;
            overflow-y: auto;
          "
        >
          <label
            v-for="(ch, idx) in wizardChannels"
            :key="idx"
            class="mc-channel"
            :class="{ on: ch.selected }"
          >
            <input v-model="ch.selected" type="checkbox" />
            <span style="font-weight: 700; min-width: 72px">{{
              ch.source
            }}</span>
            <span
              style="
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                color: var(--text-2);
              "
              :title="ch.url || ch.name || ''"
              >{{ ch.name || ch.platform_id }}</span
            >
          </label>
        </div>
        <div
          v-if="wizardError"
          style="color: var(--neg); font-size: 13px; margin-top: 10px"
        >
          {{ wizardError }}
        </div>
        <div style="display: flex; gap: 10px; margin-top: 18px">
          <button
            type="button"
            class="btm-btn btm-btn-ghost"
            style="flex: 1"
            :disabled="wizardSaving"
            @click="wizardStep = 1"
          >
            Back
          </button>
          <button
            type="button"
            class="btm-btn btm-btn-primary"
            style="flex: 1"
            :disabled="wizardSaving"
            @click="doCreate"
          >
            {{ wizardSaving ? "Saving…" : "Save competitor" }}
          </button>
        </div>
      </template>
    </MSheet>

    <MToast :msg="toast" :show="!!toast" />
  </div>
</template>

<style scoped>
.mc-toolbar {
  display: flex;
  align-items: center;
  gap: 9px;
}
.mc-toolbar-ico {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  background: var(--brand-soft);
  color: var(--brand-strong);
}
.mc-rank {
  height: 16px;
  padding: 0 5px;
  border-radius: var(--pill, 999px);
  background: var(--surface-3, var(--surface-2));
  color: var(--text-3);
  font-size: 9.5px;
  font-weight: 900;
  line-height: 16px;
}
.mc-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 11px;
  border-top: 1px solid var(--border);
}
.mc-act {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-2);
  font-family: inherit;
  font-weight: 700;
  font-size: 12px;
  padding: 7px 12px;
  border-radius: var(--r-sm, 10px);
  cursor: pointer;
}
.mc-act-neg {
  color: var(--neg);
}
.mc-act-brand {
  color: var(--brand-strong);
}
.mc-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-2);
  margin-bottom: 6px;
}
.mc-input {
  width: 100%;
  height: 44px;
  padding: 0 13px;
  border: 1px solid var(--border);
  border-radius: var(--r-sm, 10px);
  background: var(--surface-2);
  color: var(--text);
  font-family: inherit;
  font-size: 14px;
}
.mc-input:focus {
  outline: none;
  border-color: var(--brand-line);
}
.mc-channel {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: var(--r-sm, 10px);
  cursor: pointer;
  font-size: 13px;
}
.mc-channel.on {
  background: var(--brand-soft);
  border-color: var(--brand-line);
}
</style>
